import logger from '../logger'

export function legacySysPsvApiToAppContext(response) {
  try {
    return {
      id: response.id,
      products: response.productSet.slots.map(slot => ({ sku: slot.products[0].variants[0].properties.sku })),
    }
  } catch (error) {
    logger('8.LYTICS mapping failed', { error })
  }
}

export function legacySysBarApiToAppContext(response) {
  try {
    return {
      id: response.id,
      products: response.products.map(product => ({ sku: product.variants[0].sku })),
    }
  } catch (error) {
    logger('8.LYTICS mapping failed', { error })
  }
}

export function viewContent(context) {
  if (context && context.appContext && context.appContext.content && context.appContext.content.type) {
    return buildPayload(context)
  }

  return {}
}

function buildPayload(context) {
  const payload = {
    id: context.appContext.content[context.appContext.content.type].id,
    content: {
      type: context.appContext.content.type,
    },
  }

  if (context.appContext.content[context.appContext.content.type].products) {
    payload.products = context.appContext.content[context.appContext.content.type].products.map(({ sku }) => ({
      sku,
    }))
  }

  return payload
}

export function contentFound(context, window) {
  if (context && context.appContext && context.appContext.content && context.appContext.content.type) {
    const source = getFetchSource(context, window)

    // The metrics being tracked here depend heavily on the source the request was resolve by,
    // i.e a request can be resolved by the browser cache, our CDN or even all the way from origin.
    // These three sources will result in entirely different performance metric values,
    // which is why it's important to be able to disitnguish and interpret them correctly.
    // Thus if we cannot clearly determine the source, it is better not to track the metrics,
    // instead of having inconsistent or watered-down values.
    const durations = source === 'unknown' ? undefined : getFetchDuration(context, window)

    const api = { ...durations, source, url: context.api }

    return {
      api: { ...api, response: { statusCode: 200 } },
      id: context.appContext.content[context.appContext.content.type].id,
      content: {
        isFound: true,
        type: context.appContext.content.type,
      },
    }
  }

  return {}
}

export function contentNotFound(context, window) {
  if (context && context.widgetType) {
    return {
      api: {
        response: { statusCode: 204 },
      },
      content: { isFound: false },
    }
  }

  return {}
}

function getPerformanceEntryByName(name, window) {
  if (name && window && window.performance) {
    const entries = window.performance.getEntriesByName(name)

    if (entries.length === 1) {
      return entries[0]
    }
  }
}

function getFetchDuration(context, window) {
  const fetchDuration = {}

  const performanceResourceTiming = getPerformanceEntryByName(context.api, window)

  if (performanceResourceTiming) {
    fetchDuration.redirectDuration = Math.ceil(
      performanceResourceTiming.redirectEnd - performanceResourceTiming.redirectStart
    )
    fetchDuration.timeToFirstByte = Math.ceil(
      performanceResourceTiming.responseStart - performanceResourceTiming.requestStart
    )
  }

  return fetchDuration
}

function getResponseHeaders(context) {
  if (context && context.appContext && context.appContext.api) {
    // If response headers are reported by 8trolley event...
    if (context.appContext.api.response && context.appContext.api.response.headers) {
      return Object.fromEntries(
        Object.entries(context.appContext.api.response.headers).map(([key, value]) => [key.toLocaleLowerCase(), value])
      )
    }

    // ...or by widget-loader
    if (context.appContext.api.responseHeaders) {
      return Object.fromEntries(
        context.appContext.api.responseHeaders
          .toLowerCase()
          .trim()
          .split(/[\r\n]+/)
          .map(line => line.split(': '))
      )
    }
  }

  // ...else there are no headers
  return null
}

function getFetchSource(context, window) {
  const performanceResourceTiming = getPerformanceEntryByName(context.api, window)

  if (performanceResourceTiming && performanceResourceTiming.transferSize === 0) {
    return 'browser'
  }

  const responseHeaders = getResponseHeaders(context)

  if (isCloudFrontCacheHit(responseHeaders)) {
    if (responseHeaders['x-8select-playout-cache'] || responseHeaders['x-8select-playout-source']) {
      return 'lookup-cache'
    }

    return 'cdn'
  }

  if (isCloudFrontRefreshHit(responseHeaders) && responseHeaders['x-8select-playout-source']) {
    return `playout-${responseHeaders['x-8select-playout-source']}-refresh`
  }

  if (
    performanceResourceTiming &&
    performanceResourceTiming.transferSize !== 0 &&
    responseHeaders &&
    responseHeaders['x-cache'] &&
    responseHeaders['x-cache'].toLowerCase().startsWith('miss')
  ) {
    if (responseHeaders['x-8select-playout-source']) {
      return `playout-${responseHeaders['x-8select-playout-source'].toLowerCase()}`
    }

    if (responseHeaders['x-8select-playout-cache']) {
      if (responseHeaders['x-8select-playout-cache'].toLowerCase().startsWith('hit')) {
        return 'playout-cache'
      }

      return 'playout-origin'
    }

    return 'origin'
  }

  return 'unknown'
}

function isCloudFrontCacheHit(responseHeaders) {
  return responseHeaders && responseHeaders['x-cache'] && responseHeaders['x-cache'].toLowerCase().startsWith('hit')
}

function isCloudFrontRefreshHit(responseHeaders) {
  return (
    responseHeaders && responseHeaders['x-cache'] && responseHeaders['x-cache'].toLowerCase().startsWith('refreshhit')
  )
}
